import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TrustIndicators = () => {
  const trustMetrics = [
    {
      id: 1,
      icon: 'Users',
      count: '10,000+',
      label: 'Happy Players',
      description: 'Verified active gaming community',
      color: 'text-blue-500'
    },
    {
      id: 2,
      icon: 'Zap',
      count: '5 Min',
      label: 'Instant Payouts',
      description: 'Fastest withdrawal in India',
      color: 'text-green-500'
    },
    {
      id: 3,
      icon: 'Shield',
      count: '100%',
      label: 'Secure Platform',
      description: 'Bank-grade security encryption',
      color: 'text-purple-500'
    },
    {
      id: 4,
      icon: 'Clock',
      count: '24/7',
      label: 'WhatsApp Support',
      description: 'Personal gaming assistant',
      color: 'text-orange-500'
    }
  ];

  const securityBadges = [
    {
      id: 1,
      name: 'SSL Secured',
      icon: 'Lock',
      description: '256-bit encryption'
    },
    {
      id: 2,
      name: 'Fair Play Certified',
      icon: 'CheckCircle',
      description: 'RNG verified games'
    },
    {
      id: 3,
      name: 'Licensed Platform',
      icon: 'Award',
      description: 'Regulated gaming'
    },
    {
      id: 4,
      name: 'Responsible Gaming',
      icon: 'Heart',
      description: 'Player protection'
    }
  ];

  const paymentPartners = [
    {
      id: 1,
      name: 'UPI',
      logo: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg',
      description: 'Instant deposits'
    },
    {
      id: 2,
      name: 'Paytm',
      logo: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg',
      description: 'Secure payments'
    },
    {
      id: 3,
      name: 'PhonePe',
      logo: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg',
      description: 'Quick transfers'
    },
    {
      id: 4,
      name: 'Google Pay',
      logo: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg',
      description: 'Easy payments'
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Shield" size={24} color="var(--color-primary)" />
            <span className="text-primary font-medium">TRUST & SECURITY</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-4">
            Why 10,000+ Players Trust Nitin
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            India's most trusted gaming platform with verified security, instant payouts, and personalized WhatsApp support for every player.
          </p>
        </div>

        {/* Trust Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {trustMetrics?.map((metric) => (
            <div key={metric?.id} className="bg-white rounded-2xl p-6 shadow-subtle hover:shadow-royal transition-royal text-center group">
              <div className="mb-4">
                <div className={`w-16 h-16 ${metric?.color?.replace('text-', 'bg-')}/20 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={metric?.icon} size={32} color={metric?.color?.replace('text-', '')} />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">{metric?.count}</div>
              <div className="text-lg font-semibold text-gray-800 mb-2">{metric?.label}</div>
              <div className="text-sm text-gray-600">{metric?.description}</div>
            </div>
          ))}
        </div>

        {/* Security Badges */}
        <div className="bg-white rounded-2xl p-8 shadow-subtle mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              Security & Compliance
            </h3>
            <p className="text-gray-600">
              Your safety and fair play are our top priorities
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {securityBadges?.map((badge) => (
              <div key={badge?.id} className="text-center group">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/30 transition-colors duration-300">
                  <Icon name={badge?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div className="font-semibold text-gray-900 mb-1">{badge?.name}</div>
                <div className="text-sm text-gray-600">{badge?.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Partners */}
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              Trusted Payment Partners
            </h3>
            <p className="text-gray-600">
              Secure and instant transactions with India's leading payment platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {paymentPartners?.map((partner) => (
              <div key={partner?.id} className="bg-white rounded-xl p-4 shadow-subtle hover:shadow-royal transition-royal text-center group">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3 overflow-hidden">
                  <Image
                    src={partner?.logo}
                    alt={partner?.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="font-semibold text-gray-900 mb-1">{partner?.name}</div>
                <div className="text-sm text-gray-600">{partner?.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Statement */}
        <div className="text-center mt-12">
          <div className="bg-gradient-royal rounded-2xl p-8 text-white">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Icon name="Award" size={32} color="white" />
              <span className="text-2xl font-heading font-bold">Certified Excellence</span>
            </div>
            <p className="text-xl mb-6 opacity-90">
              "Nitin Online Hub has maintained a 99.9% customer satisfaction rate with zero security incidents since inception."
            </p>
            <div className="flex items-center justify-center space-x-8">
              <div className="text-center">
                <div className="text-2xl font-bold">4.9/5</div>
                <div className="text-sm opacity-80">Player Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">₹50Cr+</div>
                <div className="text-sm opacity-80">Paid to Winners</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">3 Years</div>
                <div className="text-sm opacity-80">Trusted Service</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;